import { useState, useEffect, useCallback } from 'react'
import toast from 'react-hot-toast'
import {
  Loader2, UserPlus, Users, Shield, Clock, X, ToggleLeft, ToggleRight,
  Mail, AlertTriangle, Building2, ArrowLeft, RefreshCw, Pencil, Lock, Unlock, ShieldCheck, Smartphone,
} from 'lucide-react'
import {
  getUsers, updateUserStatus, updateUserRole, unlockUser, resetUserMfa,
  inviteUser, getInvitations, cancelInvitation, resendInvitation,
} from '@/api/enterprise'
import TenantDetailsModal from '@/components/common/TenantDetailsModal'
import { useAuth } from '@/auth/AuthContext'
import { useNavigate } from 'react-router-dom'
import type { EnterpriseUser, Invitation } from '@/types'

const ROLES = [
  { value: 'ENTERPRISE_ADMIN', label: 'Enterprise Admin' },
  { value: 'PROJECT_OWNER', label: 'Project Owner' },
  { value: 'DEVELOPER', label: 'Developer' },
]

export default function UsersPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const isAdmin = user?.role === 'ENTERPRISE_ADMIN'
  // Organization details are editable by this tenant's own enterprise admin
  // (its func-admin) and by a platform instance admin.
  const canEditTenant = isAdmin || !!user?.is_instance_admin
  const [showTenantModal, setShowTenantModal] = useState(false)
  const [users, setUsers] = useState<EnterpriseUser[]>([])
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [tenantName, setTenantName] = useState('')
  const [tenantDomain, setTenantDomain] = useState('')
  const [loading, setLoading] = useState(true)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('DEVELOPER')
  const [inviting, setInviting] = useState(false)
  const [busyUserId, setBusyUserId] = useState<string | null>(null)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [usersRes, invRes] = await Promise.all([
        getUsers(),
        getInvitations().catch(() => ({ invitations: [] })),
      ])
      setTenantName(usersRes.tenant_name || '')
      setTenantDomain(usersRes.tenant_domain || '')
      setUsers(usersRes.users || [])
      setInvitations(invRes.invitations || [])
    } catch {
      toast.error('Failed to load user data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Deactivating a member blocks them from signing in to twasta.ai at all and
  // drops their live session immediately, so it gets an explicit confirm.
  // Scope is this tenant only — the backend rejects any other tenant's user.
  const handleToggleStatus = useCallback(async (target: EnterpriseUser) => {
    if (target.is_current_user) {
      toast.error('Cannot deactivate your own account')
      return
    }
    const deactivating = target.is_active
    const ok = window.confirm(
      deactivating
        ? `Deactivate ${target.user_name} (${target.email})?\n\nThey will be signed out immediately and will not be able to log in to twasta.ai until you reactivate them.`
        : `Reactivate ${target.user_name} (${target.email})?\n\nThey will be able to log in again.`
    )
    if (!ok) return
    setBusyUserId(target.user_id)
    try {
      const res = await updateUserStatus(target.user_id, !target.is_active)
      toast.success(
        deactivating
          ? `${target.user_name} deactivated${res?.sessions_ended ? ' and signed out' : ''}`
          : `${target.user_name} reactivated`
      )
      loadData()
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to update user status'
      toast.error(msg, { duration: 6000 })
    } finally {
      setBusyUserId(null)
    }
  }, [loadData])

  // A member locked out by too many failed sign-ins. They can also unlock it
  // themselves from the emailed link or by resetting their password.
  const handleUnlock = useCallback(async (target: EnterpriseUser) => {
    setBusyUserId(target.user_id)
    try {
      await unlockUser(target.user_id)
      toast.success(`${target.user_name} can sign in again`)
      loadData()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to unlock the account')
    } finally {
      setBusyUserId(null)
    }
  }, [loadData])

  // Lost phone: remove their authenticator and sign them out; they set up a
  // new one at their next sign-in.
  const handleResetMfa = useCallback(async (target: EnterpriseUser) => {
    if (!window.confirm(`Reset two-factor sign-in for ${target.user_name}?\n\nTheir authenticator and recovery codes stop working, they are signed out, and they set up a new authenticator at their next sign-in.`)) return
    setBusyUserId(target.user_id)
    try {
      await resetUserMfa(target.user_id)
      toast.success(`Two-factor sign-in reset for ${target.user_name}`)
      loadData()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to reset two-factor sign-in')
    } finally {
      setBusyUserId(null)
    }
  }, [loadData])

  const handleRoleChange = useCallback(async (userId: string, role: string) => {
    try {
      await updateUserRole(userId, role)
      toast.success('Role updated')
      loadData()
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to update role'
      toast.error(msg)
    }
  }, [loadData])

  const handleInvite = useCallback(async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address')
      return
    }
    setInviting(true)
    try {
      const res = await inviteUser(inviteEmail.trim(), inviteRole)
      if (res.email_sent === false) {
        toast.error(res.message || 'Invitation created but email delivery failed', { duration: 6000 })
        loadData()
      } else {
        toast.success('Invitation sent!')
        setShowInviteModal(false)
        setInviteEmail('')
        setInviteRole('DEVELOPER')
        loadData()
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to send invitation'
      toast.error(msg, { duration: 6000 })
    } finally {
      setInviting(false)
    }
  }, [inviteEmail, inviteRole, loadData])

  const handleCancelInvitation = useCallback(async (id: number) => {
    try {
      await cancelInvitation(id)
      toast.success('Invitation cancelled')
      loadData()
    } catch {
      toast.error('Failed to cancel invitation')
    }
  }, [loadData])

  const handleResendInvitation = useCallback(async (id: number) => {
    try {
      await resendInvitation(id)
      toast.success('Invitation resent!')
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to resend invitation'
      toast.error(msg)
    }
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100%' }}>
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
          <span style={{ color: 'var(--color-text-secondary)' }}>Loading users...</span>
        </div>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      {/* Invite modal (admin only) */}
      {isAdmin && showInviteModal && (
        <div className="overlay" onClick={() => setShowInviteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Invite User</h3>
              <button className="modal-close" onClick={() => setShowInviteModal(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="flex flex-col gap-4">
              <div>
                <label>Email Address</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleInvite() }}
                  autoFocus
                />
              </div>
              <div>
                <label>Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                >
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowInviteModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleInvite}
                disabled={inviting || !inviteEmail.trim()}
              >
                {inviting ? (
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Mail size={14} />
                )}
                {inviting ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            className="btn btn-ghost btn-icon"
            onClick={() => navigate(-1)}
            title="Go back"
            style={{ width: 32, height: 32, flexShrink: 0 }}
          >
            <ArrowLeft size={18} />
          </button>
          <Users size={24} style={{ color: 'var(--color-accent)' }} />
          <div>
            <h2 style={{ fontSize: '1.5625rem', fontWeight: 600 }}>Team Management</h2>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', margin: 0 }}>
              {isAdmin ? 'Manage users, roles, and invitations' : 'View your team members'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn btn-ghost"
            onClick={() => navigate('/enterprise/security')}
            title="Failed sign-in lockout, password rules, session timeouts and signed-in sessions"
          >
            <ShieldCheck size={16} />
            Security
          </button>
          {isAdmin && (
            <button
              className="btn btn-primary"
              onClick={() => setShowInviteModal(true)}
            >
              <UserPlus size={16} />
              Invite User
            </button>
          )}
        </div>
      </div>

      {/* Tenant info */}
      {tenantName && (
        <div className="card mb-6 flex items-center gap-3" style={{ padding: '0.875rem 1.25rem' }}>
          <Building2 size={20} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ fontWeight: 600, fontSize: '1rem' }}>{tenantName}</span>
            {tenantDomain && (
              <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginLeft: '0.5rem' }}>
                {tenantDomain}
              </span>
            )}
          </div>
          {canEditTenant && (
            <button
              className="btn btn-ghost"
              onClick={() => setShowTenantModal(true)}
              title="Edit organization name, email domain, website, GST and SSO enterprise"
              style={{ flexShrink: 0 }}
            >
              <Pencil size={14} />
              Edit Organization
            </button>
          )}
        </div>
      )}

      {showTenantModal && (
        <TenantDetailsModal
          open
          onClose={() => setShowTenantModal(false)}
          onSaved={(t) => {
            setTenantName(t.tenant_name)
            setTenantDomain(t.email_domain)
          }}
        />
      )}

      {/* Users table */}
      <div className="card mb-6" style={{ padding: 0 }}>
        <div className="card-header" style={{ padding: '0.875rem 1.25rem', margin: 0, borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0' }}>
          <span className="card-title">
            Users ({users.length})
            {users.some((u) => !u.is_active) && (
              <span style={{ color: 'var(--color-text-secondary)', fontWeight: 400, marginLeft: '0.5rem', fontSize: '0.875rem' }}>
                {users.filter((u) => !u.is_active).length} deactivated
              </span>
            )}
          </span>
        </div>
        <div className="table-container" style={{ border: 'none', borderRadius: 0 }}>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last Login</th>
                {isAdmin && <th style={{ width: 150 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id}>
                  <td>
                    <div className="flex items-center gap-2">
                      <span style={{ fontWeight: 500 }}>{u.user_name}</span>
                      {u.is_current_user && (
                        <span className="badge badge-info" style={{ fontSize: '0.8125rem' }}>You</span>
                      )}
                    </div>
                  </td>
                  <td style={{ color: 'var(--color-text-secondary)' }}>{u.email}</td>
                  <td>
                    {!isAdmin || u.is_current_user ? (
                      <span className="badge badge-info">{u.role_display}</span>
                    ) : (
                      <select
                        value={u.role}
                        onChange={(e) => handleRoleChange(u.user_id, e.target.value)}
                        style={{
                          padding: '0.25rem 1.75rem 0.25rem 0.5rem',
                          fontSize: '0.8125rem',
                          width: 'auto',
                          background: 'var(--color-bg)',
                        }}
                      >
                        {ROLES.map((r) => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td>
                    <span
                      className={`badge ${u.is_active ? 'badge-success' : 'badge-error'}`}
                      title={
                        !u.is_active && u.deactivated_at
                          ? `Deactivated ${new Date(u.deactivated_at).toLocaleDateString()}${u.deactivated_by_name ? ` by ${u.deactivated_by_name}` : ''}`
                          : undefined
                      }
                    >
                      {u.is_active ? 'Active' : 'Deactivated'}
                    </span>
                    {!u.is_active && (
                      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                        Cannot sign in
                      </div>
                    )}
                    {u.mfa_enabled && (
                      <div className="flex items-center gap-1"
                        style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2 }}
                        title="Signs in with a code from an authenticator app">
                        <Smartphone size={12} /> Two-factor on
                      </div>
                    )}
                    {u.is_active && u.locked && (
                      <div
                        className="flex items-center gap-1"
                        style={{ fontSize: '0.8125rem', color: 'var(--color-error)', marginTop: 2 }}
                        title={u.locked_at ? `Locked ${new Date(u.locked_at + 'Z').toLocaleString()}` : undefined}
                      >
                        <Lock size={12} /> Locked — too many failed sign-ins
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                    {u.last_login_at
                      // Stored as naive UTC: mark it so the browser shows local time.
                      ? new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(u.last_login_at) ? u.last_login_at : `${u.last_login_at}Z`).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })
                      : 'Never'}
                  </td>
                  {isAdmin && (
                    <td>
                      {u.locked && (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleUnlock(u)}
                          disabled={busyUserId === u.user_id}
                          title="Clear the lock so they can sign in again"
                        >
                          <Unlock size={16} />
                          Unlock
                        </button>
                      )}
                      {u.mfa_enabled && !u.is_current_user && (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleResetMfa(u)}
                          disabled={busyUserId === u.user_id}
                          title="Lost phone? Remove their authenticator; they set up a new one at next sign-in"
                        >
                          <Smartphone size={16} />
                          Reset 2FA
                        </button>
                      )}
                      {!u.is_current_user && (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleToggleStatus(u)}
                          // Platform operators are protected server-side too;
                          // disabling here avoids a round-trip that only 403s.
                          disabled={busyUserId === u.user_id || (u.is_instance_admin && u.is_active)}
                          title={
                            u.is_instance_admin && u.is_active
                              ? 'This user is a platform administrator and cannot be deactivated here'
                              : u.is_active ? 'Deactivate user — blocks sign-in' : 'Reactivate user'
                          }
                        >
                          {busyUserId === u.user_id ? (
                            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                          ) : u.is_active ? (
                            <ToggleRight size={18} style={{ color: 'var(--color-success, #1a7f37)' }} />
                          ) : (
                            <ToggleLeft size={18} style={{ color: 'var(--color-text-secondary)' }} />
                          )}
                          {u.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 6 : 5} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-secondary)' }}>
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pending invitations */}
      {invitations.length > 0 && (
        <div className="card" style={{ padding: 0 }}>
          <div className="card-header" style={{ padding: '0.875rem 1.25rem', margin: 0, borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0' }}>
            <span className="card-title flex items-center gap-2">
              <Clock size={16} />
              Pending Invitations ({invitations.length})
            </span>
          </div>
          <div className="table-container" style={{ border: 'none', borderRadius: 0 }}>
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Invited By</th>
                  <th>Date</th>
                  <th>Expires</th>
                  {isAdmin && <th style={{ width: 180 }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {invitations.map((inv) => (
                  <tr key={inv.id}>
                    <td style={{ fontWeight: 500 }}>{inv.email}</td>
                    <td>
                      <span className="badge badge-info">{inv.role_display}</span>
                    </td>
                    <td style={{ color: 'var(--color-text-secondary)' }}>{inv.invited_by}</td>
                    <td style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                      {new Date(inv.invitation_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </td>
                    <td>
                      {inv.is_expired ? (
                        <span className="badge badge-error flex items-center gap-1">
                          <AlertTriangle size={10} /> Expired
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                          {new Date(inv.expiry_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </td>
                    {isAdmin && (
                      <td>
                        <div className="flex items-center gap-1">
                          {!inv.is_expired && (
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => handleResendInvitation(inv.id)}
                              title="Resend invitation"
                            >
                              <RefreshCw size={14} style={{ color: 'var(--color-accent)' }} />
                              Resend
                            </button>
                          )}
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleCancelInvitation(inv.id)}
                            title="Cancel invitation"
                          >
                            <X size={14} style={{ color: 'var(--color-error)' }} />
                            Cancel
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Role descriptions */}
      <div className="card mt-6" style={{ background: 'var(--color-bg-secondary)' }}>
        <div className="flex items-center gap-2 mb-3">
          <Shield size={16} style={{ color: 'var(--color-accent)' }} />
          <h4 style={{ fontWeight: 600, fontSize: '1rem' }}>Role Descriptions</h4>
        </div>
        <div className="flex flex-col gap-2">
          <div>
            <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>Enterprise Admin</span>
            <p style={{ fontSize: '0.8125rem', margin: '2px 0 0' }}>
              Full access to all features including user management, billing, and settings.
            </p>
          </div>
          <div>
            <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>Project Owner</span>
            <p style={{ fontSize: '0.8125rem', margin: '2px 0 0' }}>
              Can create and manage projects, use all design and generation tools.
            </p>
          </div>
          <div>
            <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>Developer</span>
            <p style={{ fontSize: '0.8125rem', margin: '2px 0 0' }}>
              Can view and edit assigned projects, use the source editor and AI assistant.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
